import type { ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  type AutocompleteProps,
} from '@mui/material';
import { Listbox } from '@features/effector-form/components/listbox';
import { withControl } from '@features/effector-form/hoc/withControl';
import * as Styled from './styled';
import type { SelectControlType, SelectOption } from './types';

type MultipleSelectAutocompleteProps = AutocompleteProps<
  SelectOption,
  true,
  boolean,
  false
>;

const MultipleSelectAutocomplete =
  Styled.Autocomplete as ComponentType<MultipleSelectAutocompleteProps>;

export const MultipleSelectControl = withControl<SelectControlType>(
  ({
    value,
    setValue,
    error,
    label,
    required,
    readOnly,
    disabled,
    options,
    isError,
    placeholder,
    optionalText,
    notFulfilledText,
    clearable = true,
    renderOption,
    withSelectAll = false,
    filterOptions,
    selectAllLabel,
    limitTags,
  }) => {
    const { t } = useTranslation();

    const selectOptions = !options ? [] : options;
    const selectedOptions: SelectOption[] = Array.isArray(value) ? value : [];

    const isOptionEqual = (option: SelectOption, selectedOption: SelectOption) =>
      option.id === selectedOption.id && option.label === selectedOption.label;

    const isAllOptionsSelected =
      selectOptions.length > 0 &&
      selectOptions.every((option) =>
        selectedOptions.some((selectedOption) => isOptionEqual(option, selectedOption))
      );

    const selectAllOption: SelectOption = {
      id: '__select_all__',
      label: selectAllLabel || t('SelectAllEmployees'),
    };

    const optionsWithSelectAll =
      withSelectAll && selectOptions.length
        ? [selectAllOption, ...selectOptions]
        : selectOptions;

    const handleChange = (_: unknown, newValue: SelectOption[]) => {
      const hasSelectAll = newValue.some((item) => item === selectAllOption);

      if (hasSelectAll && !isAllOptionsSelected) {
        setValue(selectOptions);
        return;
      }

      setValue(newValue.filter((item) => item !== selectAllOption));
    };

    return (
      <FormControl required={required} disabled={disabled}>
        {label && <FormLabel error={isError}>{label}</FormLabel>}
        <MultipleSelectAutocomplete
          multiple
          limitTags={limitTags}
          disableCloseOnSelect
          value={selectedOptions}
          onChange={handleChange}
          options={optionsWithSelectAll}
          filterOptions={filterOptions}
          slotProps={{
            listbox: {
              component: Listbox,
            },
          }}
          disableClearable={!clearable}
          renderInput={({ InputProps, ...textFieldParams }): JSX.Element => (
            <Styled.TextField
              {...textFieldParams}
              slotProps={{
                input: {
                  ...InputProps,
                  readOnly,
                  placeholder,
                  error: isError,
                  endAdornment: readOnly ? null : InputProps.endAdornment,
                },
              }}
            />
          )}
          readOnly={readOnly}
          disabled={disabled}
          getOptionDisabled={(option) =>
            option === selectAllOption && isAllOptionsSelected
          }
          renderOption={(optionProps, option, state, ownerState) => {
            const { key: optionKey, ...liProps } = optionProps;

            if (option === selectAllOption) {
              return [
                <li {...liProps} key={optionKey}>
                  {option.label}
                </li>,
                <Styled.SelectAllDividerItem key="select-all-divider">
                  <Divider />
                </Styled.SelectAllDividerItem>,
              ];
            }

            return renderOption ? (
              renderOption(optionProps, option, state, ownerState)
            ) : (
              <li {...liProps} key={option.id}>
                {option.label}
              </li>
            );
          }}
          isOptionEqualToValue={isOptionEqual}
          {...(readOnly &&
            !value?.length && {
              renderInput: () => (
                <Styled.Placeholder>{notFulfilledText}</Styled.Placeholder>
              ),
            })}
        />
        {error && <FormHelperText error>{error}</FormHelperText>}
        {optionalText && <FormHelperText>{optionalText}</FormHelperText>}
      </FormControl>
    );
  }
);
